import type * as Api from 'src/api'

/**
 * Admin access rules. The backend authorises administrators by `role.code` and by
 * `permissions[]` entries with `action: 'view'`, and answers 403 otherwise — menus, tabs,
 * routes and write controls must follow the same rules so nothing visible leads to a 403.
 *
 * While the backend predates role-based access, profiles carry no `role` attribute at all
 * (`undefined`); those fall back to the previous degree-based rules. `role: null` means an
 * unknown group and grants nothing.
 */

export const ADMIN_ROLE_CODES = [
  'GUEST',
  'LECTURER',
  'STUDENT',
  'ADMINISTRATOR',
  'SYSTEM_VIEWER',
  'FACULTY_ADMINISTRATOR',
  'FACULTY_MODERATOR',
  'SCHEDULE_VIEWER',
  'STUDENT_VIEWER',
  'SUPPLEMENT_ADMINISTRATOR',
  'SUPPLEMENT_VIEWER',
  'REGISTRATOR',
  'RESEARCH_ADMINISTRATOR',
  'MAIN_ADMINISTRATOR',
] as const

export type AdminRoleCode = (typeof ADMIN_ROLE_CODES)[number]

/** The server blocks saving for these roles only on faculties and scores; everywhere else the UI must hide it. */
const READ_ONLY_ROLES: readonly AdminRoleCode[] = ['SYSTEM_VIEWER', 'FACULTY_MODERATOR', 'SUPPLEMENT_VIEWER']

export type AdminFeature =
  | 'faculties'
  | 'programs'
  | 'courses'
  | 'courseScores'
  | 'courseScoreStatistics'
  | 'courseSurveyResults'
  | 'schedules'
  | 'statistics'
  | 'users'
  | 'usersAll'
  | 'userAdministrators'
  | 'userLecturers'
  | 'userStudents'
  | 'regimes'
  | 'files'
  | 'caches'
  | 'portfolio'
  | 'mails'
  | 'bulkMessages'
  | 'statements'
  | 'statementTemplates'

type PermissionAction = 'view' | 'manage'

type AccessRule =
  | { readonly kind: 'role'; readonly roles: readonly AdminRoleCode[] }
  | { readonly kind: 'exceptRole'; readonly roles: readonly AdminRoleCode[] }
  | {
      readonly kind: 'permission'
      readonly entity: string
      readonly entityKey: string
      readonly entityValue?: string
      readonly action: PermissionAction
    }
  | { readonly kind: 'all'; readonly rules: readonly AccessRule[] }

function roleRule(...roles: AdminRoleCode[]): AccessRule {
  return { kind: 'role', roles }
}

function exceptRoleRule(...roles: AdminRoleCode[]): AccessRule {
  return { kind: 'exceptRole', roles }
}

function permissionRule(entity: string, entityKey: string, action: PermissionAction = 'view'): AccessRule {
  return { kind: 'permission', entity, entityKey, action }
}

function allRules(...rules: AccessRule[]): AccessRule {
  return { kind: 'all', rules }
}

/** Per user type (`users_profiles_{student|lector|administrator}` · `type` · 4|2|1), as old Argus splits the users pages. */
function userTypeRule(type: 'student' | 'lector' | 'administrator', action: PermissionAction = 'view'): AccessRule {
  const entityValue = { administrator: '1', lector: '2', student: '4' }[type]
  return { kind: 'permission', entity: `users_profiles_${type}`, entityKey: 'type', entityValue, action }
}

const USERS_VIEW = permissionRule('Controller', 'Users')

/** `entity_key` is matched case-sensitively: `Courses`, `Facultets` vs `regimes`, `schedule`, `portfolio`. */
const VIEW_RULES: Readonly<Record<AdminFeature, AccessRule>> = {
  faculties: roleRule('MAIN_ADMINISTRATOR', 'SYSTEM_VIEWER', 'ADMINISTRATOR', 'RESEARCH_ADMINISTRATOR'),
  programs: permissionRule('Controller', 'Programs'),
  courses: permissionRule('Controller', 'Courses'),
  courseScores: permissionRule('courses', 'show_scores'),
  courseScoreStatistics: permissionRule('courses', 'show_scores_statistic'),
  courseSurveyResults: permissionRule('courses', 'show_survey_results'),
  schedules: permissionRule('Controller', 'schedule'),
  statistics: permissionRule('Controller', 'Statistics'),
  users: USERS_VIEW,
  // The server filters "all users" down to the types the profile may see, so the tab needs no type of its own.
  usersAll: USERS_VIEW,
  userAdministrators: allRules(USERS_VIEW, userTypeRule('administrator')),
  userLecturers: allRules(USERS_VIEW, userTypeRule('lector')),
  userStudents: allRules(USERS_VIEW, userTypeRule('student')),
  // Faculty roles get Controller·regimes from their group defaults and the server still serves it to them, but
  // regimes turn choosing on and off for the whole university, which is not theirs to change.
  regimes: allRules(
    permissionRule('Controller', 'regimes'),
    exceptRoleRule('FACULTY_ADMINISTRATOR', 'FACULTY_MODERATOR')
  ),
  // The server serves files to MAIN_ADMINISTRATOR only, even where Controller·Files·view is granted. Faculty roles
  // also get Controller·Files from their group defaults, so the entry alone does not mean Files was granted per user.
  files: roleRule('MAIN_ADMINISTRATOR'),
  caches: roleRule('MAIN_ADMINISTRATOR'),
  portfolio: permissionRule('Controller', 'portfolio'),
  mails: permissionRule('Mails', 'read'),
  // Main administrators only (Irina, 2026-11-02); the server still accepts anyone with Controller·Users·view.
  bulkMessages: roleRule('MAIN_ADMINISTRATOR'),
  statements: permissionRule('Controller', 'Statements'),
  statementTemplates: permissionRule('Controller', 'statements_templates'),
}

/** Features whose write controls need more than "can view and is not a read-only role". */
const MANAGE_RULES: Partial<Readonly<Record<AdminFeature, AccessRule>>> = {
  faculties: roleRule('MAIN_ADMINISTRATOR', 'ADMINISTRATOR'),
  userStudents: userTypeRule('student', 'manage'),
  statementTemplates: roleRule('MAIN_ADMINISTRATOR', 'ADMINISTRATOR'),
}

/** Menu order; the first accessible entry is where the admin home page and denied routes land. */
export const ADMIN_FEATURE_PATHS: readonly (readonly [AdminFeature, string])[] = [
  ['faculties', 'administrator/administration/faculties'],
  ['programs', 'administrator/administration/programs'],
  ['courses', 'administrator/administration/courses'],
  ['statements', 'administrator/administration/statements'],
  ['schedules', 'administrator/schedules'],
  ['statistics', 'administrator/statistics'],
  ['usersAll', 'administrator/users/all'],
  ['userStudents', 'administrator/users/students'],
  ['userLecturers', 'administrator/users/lecturers'],
  ['userAdministrators', 'administrator/users/administrators'],
  ['portfolio', 'administrator/portfolio'],
  ['bulkMessages', 'administrator/messages/bulk'],
  ['caches', 'administrator/sensitive/caches'],
  ['regimes', 'administrator/sensitive/regimes'],
  ['files', 'administrator/sensitive/files'],
  ['mails', 'administrator/sensitive/mails'],
  ['statementTemplates', 'administrator/general/statement-templates'],
]

/** Course detail tabs (`administration/courses/:courseId/<tab>`) that need more than `courses`. */
export const COURSE_TAB_FEATURES: Readonly<Record<string, AdminFeature>> = {
  scores: 'courseScores',
  'score-statistics': 'courseScoreStatistics',
  'survey-results': 'courseSurveyResults',
}

type AdminProfile = Api.UserProfileAdministrator

function asAdminProfile(profile: Api.UserProfile | null | undefined): AdminProfile | null {
  return profile?.type === 'UserProfileAdministrator' ? profile : null
}

function isKnownRoleCode(code: string): code is AdminRoleCode {
  return (ADMIN_ROLE_CODES as readonly string[]).includes(code)
}

/** `null` when the profile has a role attribute but no usable code (unknown group). */
function roleCodeOf(profile: AdminProfile): AdminRoleCode | null {
  const code = profile.role?.code
  return code != null && isKnownRoleCode(code) ? code : null
}

/** Pre-rollout backend: the profile has no `role` attribute at all. */
function isLegacyProfile(profile: AdminProfile): boolean {
  return profile.role === undefined
}

export function hasPermission(
  permissions: readonly Api.Permission[] | undefined,
  entity: string,
  entityKey: string,
  action: PermissionAction = 'view',
  entityValue?: string
): boolean {
  return (permissions ?? []).some(
    (permission) =>
      permission.entity === entity &&
      permission.entity_key === entityKey &&
      permission.action === action &&
      (entityValue == null || permission.entity_value === entityValue)
  )
}

function passes(profile: AdminProfile, rule: AccessRule): boolean {
  const code = roleCodeOf(profile)
  if (code == null) return false
  switch (rule.kind) {
    case 'role':
      return rule.roles.includes(code)
    case 'exceptRole':
      return !rule.roles.includes(code)
    case 'permission':
      return hasPermission(profile.permissions, rule.entity, rule.entityKey, rule.action, rule.entityValue)
    case 'all':
      return rule.rules.every((inner) => passes(profile, inner))
  }
}

// Previous degree/permission-presence rules, kept only until the backend sends `role`.
function legacyHasPermission(profile: AdminProfile, entity: string, entityKey: string): boolean {
  return (profile.permissions ?? []).some(
    (permission) => permission.entity === entity && permission.entity_key === entityKey
  )
}

function legacyCanAccess(profile: AdminProfile, feature: AdminFeature): boolean {
  const isMain = profile.degree === 'ADMINISTRATOR_MAIN_ADMINISTRATOR'
  const isFacultyAdministrator = profile.degree === 'ADMINISTRATOR_FACULTY_ADMINISTRATOR'
  const isFacultyModerator = profile.degree === 'ADMINISTRATOR_FACULTY_MODERATOR'
  const canViewCourses =
    isMain ||
    isFacultyAdministrator ||
    isFacultyModerator ||
    (profile.permissions ?? []).some(
      (permission) =>
        permission.entity === 'Controller' && permission.entity_key === 'Courses' && permission.entity_value === '*'
    )
  switch (feature) {
    case 'faculties':
      return isMain || isFacultyModerator || legacyHasPermission(profile, 'Controller', 'Facultets')
    case 'programs':
      return (
        isMain || isFacultyAdministrator || isFacultyModerator || legacyHasPermission(profile, 'Controller', 'Programs')
      )
    case 'courses':
    case 'courseScores':
    case 'courseScoreStatistics':
    case 'courseSurveyResults':
    case 'schedules':
      return canViewCourses
    case 'statistics':
      return true
    case 'users':
    case 'usersAll':
    case 'userLecturers':
    case 'userStudents':
      return legacyHasPermission(profile, 'system', 'login_as')
    case 'userAdministrators':
      return (
        isMain ||
        (profile.permissions ?? []).some(
          (permission) =>
            permission.entity === 'system' && permission.entity_key === 'login_as' && permission.entity_value === '1'
        )
      )
    case 'regimes':
    case 'files':
    case 'caches':
    case 'portfolio':
    case 'mails':
    case 'bulkMessages':
      return isMain
    case 'statements':
      return isMain || legacyHasPermission(profile, 'Controller', 'Statements')
    case 'statementTemplates':
      return isMain || legacyHasPermission(profile, 'Controller', 'statements_templates')
  }
}

export function canAccessAdminFeature(profile: Api.UserProfile | null | undefined, feature: AdminFeature): boolean {
  const admin = asAdminProfile(profile)
  if (admin == null) return false
  if (isLegacyProfile(admin)) return legacyCanAccess(admin, feature)
  return passes(admin, VIEW_RULES[feature])
}

export function canManageAdminFeature(profile: Api.UserProfile | null | undefined, feature: AdminFeature): boolean {
  const admin = asAdminProfile(profile)
  if (admin == null || !canAccessAdminFeature(admin, feature)) return false
  if (isLegacyProfile(admin)) return true
  const code = roleCodeOf(admin)
  if (code == null || READ_ONLY_ROLES.includes(code)) return false
  const manageRule = MANAGE_RULES[feature]
  return manageRule == null || passes(admin, manageRule)
}

export function isMainAdministratorProfile(profile: Api.UserProfile | null | undefined): boolean {
  const admin = asAdminProfile(profile)
  if (admin == null) return false
  if (isLegacyProfile(admin)) return admin.degree === 'ADMINISTRATOR_MAIN_ADMINISTRATOR'
  return roleCodeOf(admin) === 'MAIN_ADMINISTRATOR'
}

/** First menu destination the profile may open, or `null` when it has no admin access at all. */
export function firstAccessibleAdminPath(profile: Api.UserProfile | null | undefined): string | null {
  return ADMIN_FEATURE_PATHS.find(([feature]) => canAccessAdminFeature(profile, feature))?.[1] ?? null
}

/**
 * Feature guarding an admin pathname (without locale), or `null` for paths with no rule
 * (the admin home page itself). Course sub-tabs resolve to their own feature.
 */
export function adminFeatureForPath(pathname: string): AdminFeature | null {
  const courseTab = /^administrator\/administration\/courses\/[^/]+\/([^/]+)/.exec(pathname)?.[1]
  const courseTabFeature = courseTab != null ? COURSE_TAB_FEATURES[courseTab] : undefined
  if (courseTabFeature != null) return courseTabFeature
  const matches = ADMIN_FEATURE_PATHS.filter(([, path]) => pathname === path || pathname.startsWith(`${path}/`))
  // Longest prefix wins, so `users/administrators` is not treated as plain `users`.
  return (
    matches.reduce<(typeof matches)[number] | null>(
      (best, match) => (best == null || match[1].length > best[1].length ? match : best),
      null
    )?.[0] ?? null
  )
}

/**
 * Create pages (`…/add`, `…/add/<step>`) only make sense with write access. Returns the list page to
 * fall back to, or `null` when the path is not a create page.
 */
export function adminCreatePageParent(pathname: string): string | null {
  const index = pathname.split('/').indexOf('add')
  return index > 0 ? pathname.split('/').slice(0, index).join('/') : null
}
